import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Chip,
} from '@mui/material';
import {
  ToggleOn as StatusIcon,
  AttachMoney as BudgetIcon,
  ExpandMore as ExpandIcon,
  UnfoldLess as CollapseIcon,
  FileDownload as ExportIcon,
  ShowChart as ShowChartIcon,
  VisibilityOff as HideChartIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import { PageHeader, ContentCard } from '../components/common';
import { DateRangePicker } from '../components/DateRangePicker';
import { AdsTable, BulkStatusModal, BulkBudgetModal, DuplicateAdModal } from '../features/ads-manager';
import { AdStatus, SortField, SortDirection } from '../types/ads.types';
import adsManagerService, { DuplicateAdRequest } from '../services/adsManager.service';
import { getDefaultFilters } from '../utils/filterUtils';
import { exportToCSV } from '../utils/csv.utils';
import { getDefaultDateRange, calculateDateRangeDays } from '../utils/dateUtils';
import {
  calculateRPM,
  calculateRPC,
  calculateCPA,
  calculateCPC,
  calculatePageCTRFromBreakdown,
  calculateCoverage,
} from '../utils/adsMetrics';
import {
  useAdsManagerData,
  useBatchUpdateAdStatuses,
  useBatchUpdateAdsetBudgets,
  useSingleUpdateAdStatus,
  useSingleUpdateAdsetBudget,
  adsManagerKeys,
} from '../hooks';

type ViewMode = 'levels' | 'tabular';
type BreakdownMode = 'daily' | 'hourly';
type FilterScope = 'campaign' | 'ads';
type SearchMode = 'contains' | 'notContains';

// Common styles for control group boxes
const controlGroupStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  px: 1.5,
  py: 0.75,
  bgcolor: 'background.paper',
  border: 1,
  borderColor: 'divider',
  borderRadius: 1.5,
};

export const AdsManagerPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('levels');
  const [breakdownMode, setBreakdownMode] = useState<BreakdownMode>('daily');
  const [showGraphs, setShowGraphs] = useState(false); // Default false, can be toggled
  const [filterScope, setFilterScope] = useState<FilterScope>('ads');
  const [searchMode, setSearchMode] = useState<SearchMode>('contains');

  // Date range state (defaults to last 3 days ending yesterday)
  const [dateRange, setDateRange] = useState(getDefaultDateRange());

  // Fetch ads data using React Query
  const {
    data: ads = [],
    isLoading: loading,
    error: fetchError,
    refetch,
  } = useAdsManagerData({
    fromDate: dateRange.from,
    toDate: dateRange.to,
    enrich: true, // Fetch creative thumbnails
  });

  // Mutations for updates
  const batchUpdateStatusMutation = useBatchUpdateAdStatuses();
  const batchUpdateBudgetMutation = useBatchUpdateAdsetBudgets();
  const singleUpdateStatusMutation = useSingleUpdateAdStatus();
  const singleUpdateBudgetMutation = useSingleUpdateAdsetBudget();

  // Filters state
  const [filters, setFilters] = useState(getDefaultFilters());

  // Sorting state
  const [sortField, setSortField] = useState<SortField>('spend');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Selection state
  const [selectedAds, setSelectedAds] = useState<Set<string>>(new Set());

  // Search state (separate from filters for controlled input)
  const [searchQuery, setSearchQuery] = useState('');

  // Modal state
  const [bulkStatusModalOpen, setBulkStatusModalOpen] = useState(false);
  const [bulkBudgetModalOpen, setBulkBudgetModalOpen] = useState(false);
  const [duplicateAdModalOpen, setDuplicateAdModalOpen] = useState(false);
  const [duplicatingAd, setDuplicatingAd] = useState<{ id: string; name: string } | null>(null);

  // Expanded campaigns state (for expand all/collapse all)
  const [expandedCampaigns, setExpandedCampaigns] = useState<Set<string>>(new Set());

  // Expanded ads state (for expand all - show daily/hourly breakdown)
  const [expandedAds, setExpandedAds] = useState<Set<string>>(new Set());

  // Expanded dates state (for expand all in hourly mode)
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());

  // Loading state for budget updates (track which adset is being updated)
  const [updatingAdsetId, setUpdatingAdsetId] = useState<string | null>(null);

  // Loading state for status updates (track which ad is being updated)
  const [updatingAdId, setUpdatingAdId] = useState<string | null>(null);

  // Date range is 30 days or less - enables hourly breakdown
  const dateRangeDays = React.useMemo(() => {
    return calculateDateRangeDays(dateRange.from, dateRange.to);
  }, [dateRange]);

  const isHourlyAvailable = dateRangeDays <= 30;

  // Show error notification if fetch fails
  useEffect(() => {
    if (fetchError) {
      enqueueSnackbar((fetchError as Error).message || 'Failed to load ads', { variant: 'error' });
    }
  }, [fetchError, enqueueSnackbar]);

  // Update search filter when search query changes (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, campaign: searchQuery }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Calculate stats from filtered data
  const totalCampaigns = React.useMemo(() => {
    const campaignIds = new Set(ads.map((ad) => ad.campaignId));
    return campaignIds.size;
  }, [ads]);

  const totalAds = ads.length;
  const selectedCount = selectedAds.size;

  // Get selected ad objects for bulk operations
  const selectedAdObjects = React.useMemo(() => {
    return ads.filter((ad) => selectedAds.has(ad.adId));
  }, [ads, selectedAds]);

  // Handlers
  const handleBulkStatus = useCallback(() => {
    setBulkStatusModalOpen(true);
  }, []);

  const handleBulkStatusConfirm = useCallback(
    async (newStatus: 'ACTIVE' | 'PAUSED') => {
      const statusUpdates = Array.from(selectedAds).map((adId) => ({
        adId,
        newStatus,
      }));

      try {
        await batchUpdateStatusMutation.mutateAsync({ statusUpdates });

        enqueueSnackbar(
          `Successfully updated status for ${selectedAds.size} ad${selectedAds.size !== 1 ? 's' : ''}`,
          { variant: 'success' },
        );

        // Clear selection
        setSelectedAds(new Set());
      } catch (error: unknown) {
        enqueueSnackbar((error as Error).message || 'Failed to update statuses', { variant: 'error' });
        throw error;
      }
    },
    [selectedAds, batchUpdateStatusMutation, enqueueSnackbar],
  );

  const handleBulkBudget = useCallback(() => {
    setBulkBudgetModalOpen(true);
  }, []);

  const handleBulkBudgetConfirm = useCallback(
    async (adsetBudgets: Record<string, number>) => {
      try {
        await batchUpdateBudgetMutation.mutateAsync({ adsetBudgets });

        const adsetCount = Object.keys(adsetBudgets).length;
        enqueueSnackbar(
          `Successfully updated budget for ${adsetCount} adset${adsetCount !== 1 ? 's' : ''}`,
          { variant: 'success' },
        );

        // Clear selection
        setSelectedAds(new Set());
      } catch (error: unknown) {
        enqueueSnackbar((error as Error).message || 'Failed to update budgets', { variant: 'error' });
        throw error;
      }
    },
    [batchUpdateBudgetMutation, enqueueSnackbar],
  );

  const handleDuplicate = useCallback(
    (adId: string) => {
      // Find ad name for the modal
      const ad = ads.find((a) => a.adId === adId);
      if (ad) {
        setDuplicatingAd({ id: adId, name: ad.adName });
        setDuplicateAdModalOpen(true);
      }
    },
    [ads],
  );

  const handleDuplicateConfirm = useCallback(
    async (request: DuplicateAdRequest) => {
      if (!duplicatingAd) return;

      try {
        const response = await adsManagerService.duplicateAd(duplicatingAd.id, request);

        enqueueSnackbar(
          response.message || `Successfully duplicated ad: ${response.newName}`,
          { variant: 'success' },
        );

        // Refresh data to show the new ad
        void refetch();
      } catch (error: unknown) {
        const errorMessage = (error as Error & { response?: { data?: { details?: string } } })?.response?.data?.details || (error as Error).message || 'Failed to duplicate ad';
        enqueueSnackbar(errorMessage, { variant: 'error' });
        throw error;
      }
    },
    [duplicatingAd, enqueueSnackbar, refetch],
  );

  const handleExpandAll = useCallback(() => {
    // Expand all campaigns
    const allCampaignIds = new Set(ads.map((ad) => ad.campaignId));
    setExpandedCampaigns(allCampaignIds);

    // Expand all ads that have breakdown data
    const allAdIds = new Set(
      ads
        .filter((ad) => {
          if (breakdownMode === 'daily') {
            return ad.dailyBreakdown && ad.dailyBreakdown.length > 0;
          } else {
            return ad.hourlyBreakdown && ad.hourlyBreakdown.length > 0;
          }
        })
        .map((ad) => ad.adId)
    );
    setExpandedAds(allAdIds);

    // Expand all dates in hourly mode
    if (breakdownMode === 'hourly') {
      const allDates = new Set<string>();
      ads.forEach((ad) => {
        if (ad.hourlyBreakdown) {
          ad.hourlyBreakdown.forEach((hour) => {
            allDates.add(`${ad.adId}-${hour.date}`);
          });
        }
      });
      setExpandedDates(allDates);
    }
  }, [ads, breakdownMode]);

  const handleCollapseAll = useCallback(() => {
    setExpandedCampaigns(new Set());
    setExpandedAds(new Set());
    setExpandedDates(new Set());
  }, []);

  const handleExport = useCallback(() => {
    if (ads.length === 0) {
      enqueueSnackbar('No data to export', { variant: 'warning' });
      return;
    }

    try {
      exportToCSV({
        data: ads,
        columns: [
          { header: 'Campaign', getValue: (ad) => ad.campaignName },
          { header: 'AdSet', getValue: (ad) => ad.adsetName },
          { header: 'Ad Name', getValue: (ad) => ad.adName },
          { header: 'Ad ID', getValue: (ad) => ad.adId },
          { header: 'Advertiser', getValue: (ad) => ad.advertiserName },
          { header: 'Status', getValue: (ad) => ad.status },
          { header: 'Spend', getValue: (ad) => ad.spend.toFixed(2) },
          { header: 'Revenue', getValue: (ad) => ad.revenue.toFixed(2) },
          { header: 'P&L', getValue: (ad) => ad.pnl.toFixed(2) },
          {
            header: 'ROAS',
            getValue: (ad) => (ad.roas * 100).toFixed(2) + '%'
          },
          {
            header: 'RPM',
            getValue: (ad) => calculateRPM(ad.revenue, ad.searches).toFixed(2)
          },
          {
            header: 'RPC',
            getValue: (ad) => calculateRPC(ad.revenue, ad.clicks).toFixed(2)
          },
          {
            header: 'CPA',
            getValue: (ad) => calculateCPA(ad.spend, ad.searches).toFixed(2)
          },
          {
            header: 'CPC',
            getValue: (ad) => calculateCPC(ad.spend, ad.clicks).toFixed(2)
          },
          { header: 'Clicks', getValue: (ad) => ad.clicks.toString() },
          { header: 'Searches', getValue: (ad) => ad.searches.toString() },
          {
            header: 'Page CTR',
            getValue: (ad) => calculatePageCTRFromBreakdown(ad.dailyBreakdown).toFixed(2) + '%'
          },
          {
            header: 'Coverage',
            getValue: (ad) => calculateCoverage(ad.dailyBreakdown).toFixed(2) + '%'
          },
          {
            header: 'Created',
            getValue: (ad) => ad.adCreatedTime ? new Date(ad.adCreatedTime).toLocaleDateString() : ''
          },
          {
            header: 'Updated',
            getValue: (ad) => ad.adUpdatedTime ? new Date(ad.adUpdatedTime).toLocaleDateString() : ''
          },
          {
            header: 'Budget',
            getValue: (ad) => ad.adsetDailyBudget ? ad.adsetDailyBudget.toFixed(2) : ''
          },
        ],
        filename: `ads-manager-export-${dateRange.from}-to-${dateRange.to}`,
      });

      enqueueSnackbar(`Exported ${ads.length} ads successfully`, { variant: 'success' });
    } catch (error) {
      console.error('Export failed:', error);
      enqueueSnackbar('Failed to export data', { variant: 'error' });
    }
  }, [ads, dateRange, enqueueSnackbar]);

  const handleDateRangeChange = useCallback((newRange: { from: string; to: string }) => {
    setDateRange(newRange);
  }, []);

  // Table handlers
  const handleSort = useCallback(
    (field: SortField) => {
      if (sortField === field) {
        // Toggle direction
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        // New field, default to descending
        setSortField(field);
        setSortDirection('desc');
      }
    },
    [sortField],
  );

  const handleSelectAd = useCallback((adId: string) => {
    setSelectedAds((prev) => {
      const next = new Set(prev);
      if (next.has(adId)) {
        next.delete(adId);
      } else {
        next.add(adId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(
    (select: boolean) => {
      if (select) {
        // Select all visible ads
        setSelectedAds(new Set(ads.map((ad) => ad.adId)));
      } else {
        // Deselect all
        setSelectedAds(new Set());
      }
    },
    [ads],
  );

  const handleUpdateStatus = useCallback(
    async (adId: string, status: AdStatus) => {
      try {
        setUpdatingAdId(adId); // Set loading state for this specific ad
        await singleUpdateStatusMutation.mutateAsync({ adId, status: status as 'ACTIVE' | 'PAUSED' });
        // Wait for the query to refetch and update the UI
        await queryClient.refetchQueries({ queryKey: adsManagerKeys.ads({ fromDate: dateRange.from, toDate: dateRange.to, enrich: true }) });
        enqueueSnackbar('Status updated successfully', { variant: 'success' });
      } catch (error: unknown) {
        enqueueSnackbar((error as Error).message || 'Failed to update status', { variant: 'error' });
      } finally {
        setUpdatingAdId(null); // Clear loading state after refetch completes
      }
    },
    [singleUpdateStatusMutation, enqueueSnackbar, queryClient, dateRange],
  );

  const handleUpdateBudget = useCallback(
    async (adsetId: string, budget: number) => {
      try {
        setUpdatingAdsetId(adsetId); // Set loading state for this specific adset
        await singleUpdateBudgetMutation.mutateAsync({ adsetId, budget });
        // Wait for the query to refetch and update the UI
        await queryClient.refetchQueries({ queryKey: adsManagerKeys.ads({ fromDate: dateRange.from, toDate: dateRange.to, enrich: true }) });
        enqueueSnackbar('Budget updated successfully', { variant: 'success' });
      } catch (error: unknown) {
        enqueueSnackbar((error as Error).message || 'Failed to update budget', { variant: 'error' });
      } finally {
        setUpdatingAdsetId(null); // Clear loading state after refetch completes
      }
    },
    [singleUpdateBudgetMutation, enqueueSnackbar, queryClient, dateRange],
  );

  return (
    <Box>
      <PageHeader
        title="Ads Manager"
        subtitle="Manage Facebook campaigns, ad sets, and ads with real-time performance metrics"
      />

      <ContentCard>
        {/* Controls Bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            background: 'linear-gradient(to right, #f9fafb, #f3f4f6)',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          {/* Left side controls */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', flex: 1 }}>
            {/* Bulk Actions - only show when items are selected */}
            {selectedCount > 0 && (
              <Box sx={controlGroupStyles}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Bulk Actions:
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<StatusIcon />}
                  onClick={handleBulkStatus}
                  sx={{
                    textTransform: 'none',
                    minWidth: 'auto',
                  }}
                >
                  Status
                  <Chip
                    label={selectedCount}
                    size="small"
                    sx={{
                      ml: 1,
                      height: 20,
                      fontSize: '0.75rem',
                      bgcolor: 'white',
                      color: 'primary.main',
                      fontWeight: 700
                    }}
                  />
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<BudgetIcon />}
                  onClick={handleBulkBudget}
                  sx={{
                    textTransform: 'none',
                    minWidth: 'auto',
                  }}
                >
                  Budget
                  <Chip
                    label={selectedCount}
                    size="small"
                    sx={{
                      ml: 1,
                      height: 20,
                      fontSize: '0.75rem',
                      bgcolor: 'white',
                      color: 'success.main',
                      fontWeight: 700
                    }}
                  />
                </Button>
              </Box>
            )}

            {/* View Mode */}
            <Box sx={controlGroupStyles}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                View:
              </Typography>
              <Button
                variant={viewMode === 'levels' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setViewMode('levels')}
                sx={{ textTransform: 'none', minWidth: 'auto' }}
              >
                Levels
              </Button>
              <Button
                variant={viewMode === 'tabular' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setViewMode('tabular')}
                sx={{ textTransform: 'none', minWidth: 'auto' }}
              >
                Tabular
              </Button>
              {viewMode === 'levels' && (
                <>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="small"
                    startIcon={<ExpandIcon />}
                    onClick={handleExpandAll}
                    sx={{ textTransform: 'none', minWidth: 'auto', ml: 1 }}
                  >
                    Expand All
                  </Button>
                  <Button
                    variant="contained"
                    color="inherit"
                    size="small"
                    startIcon={<CollapseIcon />}
                    onClick={handleCollapseAll}
                    sx={{ textTransform: 'none', minWidth: 'auto' }}
                  >
                    Collapse All
                  </Button>
                </>
              )}
            </Box>

            {/* Breakdown Mode - only show if hourly is available and in levels view */}
            {viewMode === 'levels' && isHourlyAvailable && (
              <Box sx={{ ...controlGroupStyles, py: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Breakdown:
                </Typography>
                <Button
                  variant={breakdownMode === 'daily' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setBreakdownMode('daily')}
                  sx={{ textTransform: 'none', minWidth: 'auto' }}
                >
                  Daily
                </Button>
                <Button
                  variant={breakdownMode === 'hourly' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setBreakdownMode('hourly')}
                  sx={{ textTransform: 'none', minWidth: 'auto' }}
                >
                  Hourly
                </Button>
              </Box>
            )}

            {/* Graphs Toggle - only show in levels view */}
            {viewMode === 'levels' && (
              <Box sx={{ ...controlGroupStyles, py: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Graphs:
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  color="primary"
                  onClick={() => setShowGraphs(true)}
                  startIcon={<ShowChartIcon sx={{ fontSize: 16 }} />}
                  disabled={showGraphs}
                  sx={{
                    textTransform: 'none',
                    minWidth: 'auto',
                  }}
                >
                  Show
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="primary"
                  onClick={() => setShowGraphs(false)}
                  startIcon={<HideChartIcon sx={{ fontSize: 16 }} />}
                  disabled={!showGraphs}
                  sx={{
                    textTransform: 'none',
                    minWidth: 'auto',
                  }}
                >
                  Hide
                </Button>
              </Box>
            )}

            {/* Export */}
            <Box sx={controlGroupStyles}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Export:
              </Typography>
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<ExportIcon />}
                onClick={handleExport}
                sx={{ textTransform: 'none', minWidth: 'auto' }}
              >
                CSV
              </Button>
            </Box>

            {/* Date Range Picker */}
            <Box sx={controlGroupStyles}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Date:
              </Typography>
              <DateRangePicker value={dateRange} onChange={handleDateRangeChange} />
            </Box>

          </Box>

          {/* Right side - Stats */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {totalCampaigns} campaigns • {totalAds} total ads
              {selectedAds.size > 0 && (
                <Chip
                  label={`${selectedAds.size} selected`}
                  size="small"
                  color="warning"
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>
          </Box>
        </Box>

        {/* Ads Table */}
        <AdsTable
          ads={ads}
          viewMode={viewMode}
          breakdownMode={breakdownMode}
          showGraphs={showGraphs}
          filters={filters}
          filterScope={filterScope}
          searchMode={searchMode}
          sortField={sortField}
          sortDirection={sortDirection}
          selectedAds={selectedAds}
          loading={loading}
          updatingAdsetId={updatingAdsetId}
          updatingAdId={updatingAdId}
          expandedCampaigns={expandedCampaigns}
          expandedAds={expandedAds}
          expandedDates={expandedDates}
          searchQuery={searchQuery}
          onSort={handleSort}
          onSelectAd={handleSelectAd}
          onSelectAll={handleSelectAll}
          onUpdateStatus={handleUpdateStatus}
          onUpdateBudget={handleUpdateBudget}
          onDuplicate={handleDuplicate}
          onExpandedCampaignsChange={setExpandedCampaigns}
          onExpandedAdsChange={setExpandedAds}
          onExpandedDatesChange={setExpandedDates}
          onFiltersChange={setFilters}
          onSearchChange={setSearchQuery}
          onSearchModeChange={setSearchMode}
          onFilterScopeChange={setFilterScope}
        />
      </ContentCard>

      {/* Bulk Action Modals */}
      <BulkStatusModal
        open={bulkStatusModalOpen}
        selectedAds={selectedAdObjects}
        onClose={() => setBulkStatusModalOpen(false)}
        onConfirm={handleBulkStatusConfirm}
      />
      <BulkBudgetModal
        open={bulkBudgetModalOpen}
        selectedAds={selectedAdObjects}
        onClose={() => setBulkBudgetModalOpen(false)}
        onConfirm={handleBulkBudgetConfirm}
      />

      {/* Duplicate Ad Modal */}
      {duplicatingAd && (
        <DuplicateAdModal
          open={duplicateAdModalOpen}
          adId={duplicatingAd.id}
          adName={duplicatingAd.name}
          onClose={() => setDuplicateAdModalOpen(false)}
          onConfirm={handleDuplicateConfirm}
        />
      )}
    </Box>
  );
};

export default AdsManagerPage;
